import React, { useEffect, useState, useMemo } from 'react';
import { 
  ShieldCheck, Search, Users, 
  CheckCircle2, XCircle, RefreshCw,
  Eye, Edit3 // 🚨 [추가] 탭 스위칭용 아이콘
} from 'lucide-react';
import { API_BASE_URL } from '../../config';

// =============================================================================
// 📋 타입 정의
// =============================================================================
type User = {
  id: number;
  name: string;
  username: string;
  team_name: string | null;
  grade: string;
  role: string;
};

// 🚨 [수정] DB에 추가한 9개의 show_ 스위치를 타입에 포함
type Permissions = {
  edit_sales_target: boolean;
  edit_forecast: boolean;
  edit_pipeline: boolean;
  edit_company_contact: boolean;
  edit_lost_account: boolean;
  edit_cs: boolean;
  edit_wbs: boolean;
  show_dashboard_feed: boolean;
  show_sales_target: boolean;
  show_forecast: boolean;
  show_sales_analysis: boolean;
  show_pipeline: boolean;
  show_contacts: boolean;
  show_lost_account: boolean;
  show_cs_activity: boolean;
  show_wbs: boolean;
};

type UserWithPermissions = User & { permissions: Permissions };

// 🚨 [추가] 화면에 그릴 스위치 목록 (반복문으로 깔끔하게 처리하기 위함)
const EDIT_PERMS = [
  { key: 'edit_sales_target', label: '매출목표' },
  { key: 'edit_forecast', label: '매출예정표' },
  { key: 'edit_pipeline', label: '영업진행' },
  { key: 'edit_company_contact', label: '거래처/고객통합' },
  { key: 'edit_lost_account', label: '이탈거래처' },
  { key: 'edit_cs', label: 'CS팀 활동' },
  { key: 'edit_wbs', label: 'WBS' }
];

const SHOW_PERMS = [
  { key: 'show_dashboard_feed', label: '실시간 영업 피드' },
  { key: 'show_sales_target', label: '매출목표' },
  { key: 'show_forecast', label: '매출예정표' },
  { key: 'show_sales_analysis', label: '목표대비실적' }, // 추가된 목표대비실적
  { key: 'show_pipeline', label: '영업진행' },
  { key: 'show_contacts', label: '거래처/고객' },
  { key: 'show_lost_account', label: '이탈거래처' },
  { key: 'show_cs_activity', label: 'CS활동' },
  { key: 'show_wbs', label: 'WBS' }
];

const AdminSettings: React.FC = () => {
  const [users, setUsers] = useState<UserWithPermissions[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [savingId, setSavingId] = useState<number | null>(null);
  
  // 🚨 [추가] 현재 선택된 권한 탭 상태 ('edit' = 편집권한, 'show' = 노출권한)
  const [activeSettingTab, setActiveSettingTab] = useState<'edit' | 'show'>('edit');

  // =============================================================================
  // 🔄 데이터 로드 (유저 명단 + 권한 정보)
  // =============================================================================
  const loadData = async () => {
    setLoading(true);
    try {
      const uRes = await fetch(`${API_BASE_URL}/admin/users`);
      const uData = await uRes.json();
      const loadedUsers: User[] = uData?.users || [];

      let pData: Record<number, Permissions> = {};
      try {
        const pRes = await fetch(`${API_BASE_URL}/admin/permissions`);
        if (pRes.ok) pData = await pRes.json();
      } catch (e) {
        console.warn("권한 API가 아직 준비되지 않았습니다. 기본값으로 렌더링합니다.");
      }

      const combined = loadedUsers.map(user => {
        // 관리자(admin)는 무조건 모든 스위치 ON
        if (user.role === 'admin') {
          return {
            ...user,
            permissions: {
              edit_sales_target: true, edit_forecast: true, edit_pipeline: true,
              edit_company_contact: true, edit_lost_account: true, edit_cs: true, edit_wbs: true,
              show_dashboard_feed: true, show_sales_target: true, show_forecast: true,
              show_sales_analysis: true, show_pipeline: true, show_contacts: true,
              show_lost_account: true, show_cs_activity: true, show_wbs: true
            }
          };
        }
        
        // 🚨 [수정] 일반 유저는 DB값 가져오고, 없으면 edit은 false, show는 false가 기본값!
        return {
          ...user,
          permissions: pData[user.id] || {
            edit_sales_target: false, edit_forecast: false, edit_pipeline: false,
            edit_company_contact: false, edit_lost_account: false, edit_cs: false, edit_wbs: false,
            show_dashboard_feed: false, show_sales_target: false, show_forecast: false,
            show_sales_analysis: false, show_pipeline: false, show_contacts: false,
            show_lost_account: false, show_cs_activity: false, show_wbs: false
          }
        };
      });

      setUsers(combined.sort((a, b) => {
        if (a.role === 'admin') return -1;
        if (b.role === 'admin') return 1;
        return a.name.localeCompare(b.name);
      }));

    } catch (e) {
      console.error("권한 데이터 로드 실패:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // =============================================================================
  // 🖱️ 스위치 토글 핸들러 (권한 업데이트)
  // =============================================================================
  const handleToggle = async (userId: number, key: keyof Permissions, currentValue: boolean) => {
    const user = users.find(u => u.id === userId);
    if (user?.role === 'admin') {
      alert("최고 관리자(admin)의 권한은 해제할 수 없습니다.");
      return;
    }

    const newValue = !currentValue;
    
    // UI 즉시 업데이트
    setUsers(prev => prev.map(u => 
      u.id === userId ? { ...u, permissions: { ...u.permissions, [key]: newValue } } : u
    ));

    setSavingId(userId);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/permissions/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: newValue })
      });
      
      if (!res.ok) throw new Error("저장 실패");
    } catch (e) {
      console.error(e);
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, permissions: { ...u.permissions, [key]: currentValue } } : u
      ));
      alert("권한 저장에 실패했습니다. 백엔드 API를 확인해주세요.");
    } finally {
      setSavingId(null);
    }
  };

  // =============================================================================
  // 💾 검색 및 필터링
  // =============================================================================
  const filteredUsers = useMemo(() => {
    return users.filter(u => 
      u.name.includes(searchTerm) || 
      (u.team_name || '').includes(searchTerm)
    );
  }, [users, searchTerm]);

  const CustomToggle = ({ isOn, disabled, onClick }: { isOn: boolean, disabled?: boolean, onClick: () => void }) => (
    <button 
      onClick={onClick} 
      disabled={disabled}
      className={`relative w-11 h-6 rounded-full transition-all duration-300 focus:outline-none shadow-inner flex items-center px-1
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}
        ${isOn ? 'bg-emerald-500' : 'bg-slate-300'}
      `}
    >
      <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-300 flex items-center justify-center
        ${isOn ? 'translate-x-5' : 'translate-x-0'}
      `}>
        {isOn ? <CheckCircle2 size={12} className="text-emerald-500" /> : <XCircle size={12} className="text-slate-400" />}
      </div>
    </button>
  );

  // 🚨 [추가] 현재 선택된 탭에 따라 그릴 컬럼(스위치) 배열 결정
  const currentColumns = activeSettingTab === 'edit' ? EDIT_PERMS : SHOW_PERMS;

  // =============================================================================
  // 🎨 화면 렌더링
  // =============================================================================
  return (
    <div className="h-full flex flex-col gap-4 overflow-hidden bg-transparent relative font-sans animate-fade-in">
      
      {/* 상단 헤더 */}
      <section className="shrink-0 bg-white rounded-[24px] border border-slate-200 shadow-sm overflow-hidden p-6">
        <div className="flex justify-between items-start">
          <div className="flex items-start gap-4">
            <div className="bg-indigo-100 p-3 rounded-2xl text-indigo-600 shadow-inner">
              <ShieldCheck size={28} strokeWidth={2.5}/>
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">전사 권한 매트릭스</h2>
              <p className="text-xs font-bold text-slate-500 mt-1 mb-4">
                권한 설정을 통해 직원의 메뉴 접근 및 데이터 수정 범위를 제어합니다.
              </p>

              {/* 🚨 [추가] 권한 모드 스위칭 버튼 */}
              <div className="flex bg-slate-100 p-1 rounded-xl w-fit border border-slate-200 shadow-inner">
                <button
                  onClick={() => setActiveSettingTab('edit')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-black transition-all ${
                    activeSettingTab === 'edit' 
                    ? 'bg-white shadow text-indigo-600' 
                    : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Edit3 size={16} /> ✍️ 데이터 편집 권한
                </button>
                <button
                  onClick={() => setActiveSettingTab('show')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-black transition-all ${
                    activeSettingTab === 'show' 
                    ? 'bg-white shadow text-emerald-600' 
                    : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Eye size={16} /> 👁️ 메뉴 노출 권한
                </button>
              </div>

            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16}/>
              <input 
                className="pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold w-64 outline-none focus:border-indigo-400 focus:bg-white transition-all shadow-sm" 
                placeholder="이름 또는 부서 검색" 
                value={searchTerm} 
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <button onClick={loadData} className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all shadow-sm">
              <RefreshCw size={18} className={loading ? 'animate-spin text-indigo-500' : ''} />
            </button>
          </div>
        </div>
      </section>

      {/* 권한 표 (매트릭스) */}
      <section className="flex-1 bg-white rounded-[24px] border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-0">
        <div className="flex-1 overflow-auto custom-scrollbar">
          <table className="w-full text-center border-collapse">
            <thead className={`sticky top-0 z-20 text-white shadow-md transition-colors ${activeSettingTab === 'edit' ? 'bg-slate-900' : 'bg-slate-800'}`}>
              <tr>
                <th className="px-6 py-4 text-left font-black text-sm w-[15%] border-r border-slate-700/50">직원 정보</th>
                
                {/* 🚨 [추가] 동적으로 헤더 렌더링 */}
                {currentColumns.map(col => (
                  <th key={col.key} className="px-2 py-4 text-[11px] font-black uppercase tracking-wider text-slate-300 hover:text-white transition-colors break-keep">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={currentColumns.length + 1} className="py-20 text-slate-400 font-bold"><RefreshCw size={32} className="animate-spin mx-auto mb-4 opacity-50"/>데이터를 불러오는 중입니다...</td></tr>
              ) : filteredUsers.length === 0 ? (
                <tr><td colSpan={currentColumns.length + 1} className="py-20 text-slate-400 font-bold"><Users size={32} className="mx-auto mb-4 opacity-50"/>검색된 직원이 없습니다.</td></tr>
              ) : (
                filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-slate-50/80 transition-colors group">
                    {/* 1. 직원 정보 */}
                    <td className="px-6 py-4 text-left border-r border-slate-50">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-black shrink-0 shadow-sm
                          ${user.role === 'admin' ? 'bg-red-100 text-red-600' : 'bg-indigo-100 text-indigo-600'}
                        `}>
                          {user.name.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-black text-slate-800 text-sm truncate">{user.name}</span>
                            {user.role === 'admin' && <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-red-500 text-white shadow-sm">관리자</span>}
                          </div>
                          <div className="text-[11px] font-bold text-slate-400 mt-0.5 truncate">
                            {user.team_name || '미배정'} · {user.grade || '사원'}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* 🚨 [추가] 동적 스위치 렌더링 */}
                    {currentColumns.map((col) => {
                      const pKey = col.key as keyof Permissions;
                      const isOn = user.permissions[pKey];
                      return (
                        <td key={col.key} className="px-2 py-4 text-center">
                          <div className="flex flex-col items-center justify-center gap-1.5 relative">
                            <CustomToggle 
                              isOn={isOn} 
                              disabled={user.role === 'admin'} 
                              onClick={() => handleToggle(user.id, pKey, isOn)}
                            />
                            {/* 🚨 탭 상태에 따라 ON/OFF 텍스트 다르게 표시 */}
                            <span className={`text-[9px] font-black uppercase tracking-wider transition-colors
                              ${isOn ? 'text-emerald-600' : 'text-slate-300'}
                            `}>
                              {activeSettingTab === 'edit' 
                                ? (isOn ? '편집 O' : '읽기 전용') 
                                : (isOn ? '노출 O' : '숨김')}
                            </span>
                            
                            {savingId === user.id && (
                               <RefreshCw size={12} className="absolute -right-2 top-1 animate-spin text-indigo-400 opacity-50" />
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AdminSettings;